/**
 * Google Ads Adapter
 * 
 * Adapts standardized event data into Google Ads format.
 * Handles both dynamic remarketing and conversion tracking events.
 * 
 * @since 1.51.0
 */

(function (pmw, undefined) {

	/**
	 * Google Ads Data Adapter
	 */
	const GoogleAdsAdapter = {
		
		/**
		 * Main adapt method - routes to specific event adapters
		 * 
		 * @param {object} eventData - Standardized event data
		 * @param {string} eventName - Event name (add_to_cart, view_item, etc.)
		 * @param {object} context - Event context (user, session, page data)
		 * @returns {object|null} Google Ads-formatted data or null if pixel inactive
		 */
		adapt(eventData, eventName, context) {
			// Check if Google Ads is active
			if (pmw.isEmptyObject(pmwDataLayer?.pixels?.google?.ads?.conversion_ids)) {
				return null;
			}
			
			// Check if marketing tracking is allowed
			if (!pmw.googleConfigConditionsMet({type: "marketing"})) {
				return null;
			}
			
		// Map standardized event names to adapter methods
		const eventMap = {
			'view_item_list': this.adaptViewItemList,
			'add_to_cart': this.adaptAddToCart,
			'view_item': this.adaptViewItem,
			'search': this.adaptSearch,
			'login': this.adaptLogin,
			'purchase': this.adaptPurchase
		};			const adapter = eventMap[eventName];
			
			if (!adapter) {
				pmw.console.log(`No Google Ads adapter for event: ${eventName}`);
				return null;
			}
			
			try {
				return adapter.call(this, eventData, context);
			} catch (e) {
				pmw.console.error(`Google Ads adapter error for ${eventName}:`, e);
				return null;
			}
		},
		
		/**
		 * Get base data structure for Google Ads events
		 */
		getBaseData() {
			const baseData = {
				send_to: pmw.getGoogleAdsConversionIdentifiers()
			};
			
			// Add user_id if available
			if (pmwDataLayer?.user?.id?.raw) {
				baseData.user_id = pmwDataLayer.user.id.raw;
			}
			
			return baseData;
		},
		
		/**
		 * Get product item data for Google Ads
		 */
		getProductItemData(product) {
			// Ensure product and dyn_r_ids exist before accessing
			if (!product?.dyn_r_ids) {
				return null;
			}
			
			return {
				id: product.dyn_r_ids[pmwDataLayer.pixels.google.ads.dynamic_remarketing.id_type],
				google_business_vertical: pmwDataLayer.pixels.google.ads.google_business_vertical
			};
		},
		
		/**
		 * Adapt view_item_list event
		 */
		adaptViewItemList(eventData, context) {
			const product = eventData.product;
			
			// Prevent cached hits
			if (!product) {
				return null;
			}
			
			// Skip if variations should not be tracked with parent IDs
			if (
				pmwDataLayer?.shop?.variations_output &&
				product.is_variable &&
				pmwDataLayer.pixels.google.ads.dynamic_remarketing.send_events_with_parent_ids === false
			) {
				return null;
			}
			
			const itemData = this.getProductItemData(product);
			if (!itemData) {
				return null;
			}
			
			return {
				...this.getBaseData(),
				items: [itemData]
			};
		},
		
		/**
		 * Adapt add_to_cart event
		 */
		adaptAddToCart(eventData, context) {
			const product = eventData.product;
			
			const itemData = this.getProductItemData(product);
			if (!itemData) {
				return null;
			}
			
			return {
				...this.getBaseData(),
				value: product.quantity * product.price,
				items: [{
					...itemData,
					quantity: product.quantity,
					price: product.price
				}]
			};
		},
		
		/**
		 * Adapt view_item event
		 */
		adaptViewItem(eventData, context) {
			const product = eventData.product;
			const data = this.getBaseData();
			
			if (product) {
				const itemData = this.getProductItemData(product);
				if (!itemData) {
					// No valid product data, return null to skip this event
					return null;
				}
				
				data.value = (product.quantity ? product.quantity : 1) * product.price;
				data.items = [{
					...itemData,
					quantity: (product.quantity ? product.quantity : 1),
					price: product.price
				}];
			}
			
			return data;
		},
		
		/**
		 * Adapt search event
		 */
		adaptSearch(eventData, context) {
			const products = [];
			
			Object.values(pmwDataLayer.products).forEach(product => {
				// Skip variations if configured
				if (
					pmwDataLayer?.shop?.variations_output &&
					product.is_variable &&
					pmwDataLayer.pixels.google.ads.dynamic_remarketing.send_events_with_parent_ids === false
				) {
					return;
				}
				
				const itemData = this.getProductItemData(product);
				if (!itemData) {
					return;
				}
				
				products.push({
					...itemData
				});
			});
			
			return {
				...this.getBaseData(),
				items: products
			};
		},
		
		/**
		 * Adapt login event
		 */
		adaptLogin(eventData, context) {
			return this.getBaseData();
		},
		
		/**
		 * Adapt purchase event (order received page)
		 * 
		 * Google Ads has two types of purchase events:
		 * 1. Remarketing purchase event
		 * 2. Conversion tracking event
		 * 
		 * This adapter returns both as separate events
		 */
		adaptPurchase(eventData, context) {
			if (!pmwDataLayer?.order) {
				pmw.console.error('Order data not available for Google Ads Purchase event');
				return null;
			}
			
			// Return both remarketing and conversion data
			return {
				// Remarketing purchase event
				remarketing: {
					send_to: pmw.getGoogleAdsConversionIdentifiers(),
					value: pmwDataLayer.order.value.marketing,
					items: pmw.getGoogleAdsRegularOrderItems()
				},
				
				// Conversion tracking event
				conversion: {
					send_to: pmw.getGoogleAdsConversionIdentifiersWithLabel(),
					transaction_id: pmwDataLayer.order.number,
					value: pmwDataLayer.order.value.marketing,
					currency: pmwDataLayer.order.currency,
					new_customer: pmwDataLayer.order.new_customer,
					...(pmwDataLayer?.order?.value?.ltv?.marketing && {
						customer_lifetime_value: pmwDataLayer.order.value.ltv.marketing
					}),
					...(pmwDataLayer?.user?.id?.raw && {
						user_id: pmwDataLayer.user.id.raw
					}),
					// Cart data if merchant center is configured
					...(pmwDataLayer?.order?.aw_merchant_id && {
						discount: pmwDataLayer.order.discount,
						aw_merchant_id: pmwDataLayer.order.aw_merchant_id,
						aw_feed_country: pmwDataLayer.order.aw_feed_country,
						aw_feed_language: pmwDataLayer.order.aw_feed_language,
						items: pmw.getGoogleAdsRegularOrderItems()
					}),
					// Custom variables
					...(pmwDataLayer?.order?.google?.ads?.custom_variables && 
						pmwDataLayer.order.google.ads.custom_variables
					)
				}
			};
		}
	};

	// Register the adapter
	pmw.registerAdapter('google_ads', GoogleAdsAdapter, {
		isActive: () => {
			return pmwDataLayer?.pixels?.google?.ads?.conversion_ids && 
				Object.keys(pmwDataLayer.pixels.google.ads.conversion_ids).length > 0;
		}
	});

}(window.pmw = window.pmw || {}));
